import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { connect } from 'react-redux';
import './style.css';

import {
  fetchCasesWithClinical,
  fetchCasesWithBiospecimen,
  fetchCasesWithData,
  fetchFilesUploaded,
  fetchQCErrors,
} from 'ducks/widgets';
import { setMessage } from 'ducks/message';
import Row from 'components/UIKit/Flex/Row';
import Column from 'components/UIKit/Flex/Column';
import Card from 'components/UIKit/Card';
import CenteredLayout from 'components/layouts/CenteredLayout';
import ReviewIcon from 'components/icons/ReviewIcon';
import { SubmittedIcon } from 'components/icons';
import { fetchTransaction } from 'ducks/details';
import UploadAndValidate from 'components/Dashboard/UploadAndValidate';
import ReviewAndSubmit from 'components/Dashboard/ReviewAndSubmit';
import DataDownloadModalButton from 'components/buttons/DataDownloadModalButton';
import { AuthorizedForProject } from 'components/Security';
import DonutRow from 'components/DonutRow';
import TransactionDetails from 'components/DetailsPanels/TransactionDetails/';
import { SpinnerOverlay } from 'components/UIKit/Spinner';
import CloseDetailsLink from 'components/Links/CloseDetailsLink';
import { getSubmittableFileDefinitions } from 'utils/dictionaryHelpers';
import { resetWorkflow } from 'ducks/workflow';
import { projectIsSubmitting } from 'ducks/projects';
import { Tooltip } from 'components/UIKit/Tooltip';
import OpenButtonContainer from 'containers/workflow/OpenButtonContainer';
import SubmitButtonContainer from 'containers/workflow/SubmitButtonContainer';
import withRouting from 'utils/withRouting';

const EmailLink = ({ url, children, ...props }) => (
  <a {...props} href={url}>
    {children}
  </a>
);

const getLinks = (defs) => {
  const dictByCat = _.groupBy(defs, 'category');
  const clinicalLinks = (dictByCat.clinical || []).reduce((acc, node) => {
    // with_links expects plural field names (in links.backref)
    // with_path_to expects singular field names (in node.id)
    const caseBackrefs = (node.links || [])
      .filter(link => link.target_type === 'case')
      .map(link => link.backref);

    if (caseBackrefs.length) {
      return { ...acc, withLink: [...acc.withLink, ...caseBackrefs] };
    }
    return { ...acc, withPathTo: [...acc.withPathTo, node.id] };
  }, { withLink: [], withPathTo: [] });

  const biospecimenLinks = (dictByCat.biospecimen || []).map(node => node.id);
  const dataBundleLinks = defs.filter(d => d.submittable).filter(l => l.category === 'data_file' || l.category === 'metadata_file').map(p => p.id);
  const fileStates = ((((defs.find(n => n.id === '_definitions') || {}).file_state) || {}).enum) || [];

  return {
    clinicalLinks, biospecimenLinks, dataBundleLinks, fileStates,
  };
}


const Dashboard = (props) => {
  const [submitFailed, setSubmitFailed] = useState(false);
  const { did } = props.router.location.query;

  const {
    widgets, router, projects, uploadTransactions, details, workflow, entityDefinitions, dispatch,
  } = props;
  const { program, project: projectParams } = router.params;

  const projectId = `${router.params.program}-${router.params.project}`;
  const project = projects.items.find(p => p.project_id === projectId) || {};

  const submitIsDisabled = () => !project.submission_enabled
  || project.request_submission
  || !(router.params.program && router.params.project);

  const defs = useMemo(() => entityDefinitions.defs, [JSON.stringify(entityDefinitions.defs)]);

  const fetchDashboardData = useCallback(() => {
    if ((defs || []).length > 0) {
      const projectId = [`${program}-${projectParams}`];
      const links = getLinks(entityDefinitions.defs);
      const vars = { projectId, links };

      dispatch(fetchCasesWithClinical(vars));
      dispatch(fetchCasesWithBiospecimen(vars));
      dispatch(fetchCasesWithData(vars));
      dispatch(fetchFilesUploaded({
        ...vars,
        submittedFileTypes: getSubmittableFileDefinitions({ defs }).map(i => i.id),
      }));
      dispatch(fetchQCErrors({ program, project: projectParams }));
    }
  }, [defs, program, projectParams, dispatch]);

  useEffect(() => {
    if (did) {
      dispatch(fetchTransaction({ id: did }))
    }
  }, [did, dispatch]);

  useEffect(() => {
    if (did) {
      const { data } = uploadTransactions;
      if (data.items && data.items.length) {
        const { items } = data;

        const nextTransaction = items[0];
        const txMoment = moment(nextTransaction.created_datetime);
        const isIncoming = moment().subtract(2, 'seconds').isBefore(txMoment);
        if (isIncoming) {
          dispatch(fetchTransaction({ id: did }));
        }
      }
    }
  }, [did, dispatch, uploadTransactions]);

  useEffect(() => {
    const projectId = `${props.router.params.program}-${props.router.params.project}`;
    const project = props.projects.items.find(p => p.project_id === projectId) || {};

    if (!(project.request_submission || project.release_requested || project.released)) {
      setSubmitFailed(false);
    }
  }, [props.router.params, props.projects.items])

  useEffect(() => {
    let { data,  otherTransactions } = uploadTransactions;
    let { items } = data;
    if (items && items.length) {
      const nextTransaction = items[0];
      const txMoment = moment(nextTransaction.created_datetime);
      const isIncoming = moment().subtract(2, 'seconds').isBefore(txMoment);
      const hasNewCommittedTx = isIncoming && nextTransaction.state === 'SUCCEEDED' && !nextTransaction.closed && !nextTransaction.is_dry_run;
      if (hasNewCommittedTx) {
        fetchDashboardData();
      }
    }

    if (workflow?.transaction_id) {
      const lastSubmitTransaction = _.find(otherTransactions.items, ({ id, type }) => id === `${workflow.transaction_id}` && type === 'submit');
      if (lastSubmitTransaction && lastSubmitTransaction.state !== 'PENDING') {
        dispatch(projectIsSubmitting(lastSubmitTransaction.project_id, false));
        if (lastSubmitTransaction.state === 'FAILED' && !submitFailed) {
          dispatch(setMessage(`${lastSubmitTransaction.project_id} request for harmonization attempt failed`));
          setSubmitFailed(true);
        }
        if (lastSubmitTransaction.state === 'ERRORED' && !submitFailed) {
          dispatch(setMessage(`${lastSubmitTransaction.project_id} request for harmonization attempt errored`));
          setSubmitFailed(true);
        }
        if (lastSubmitTransaction.state === 'SUCCEEDED') {
          dispatch(setMessage(`${lastSubmitTransaction.project_id} harmonization is requested`));
        }
        dispatch(resetWorkflow());
      }
    }
  }, [uploadTransactions, submitFailed, workflow?.transaction_id, dispatch, fetchDashboardData]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return (
    <Row>
      <CenteredLayout>
        {project.submission_enabled === false && (
          <Card style={{ borderColor: 'rgb(187, 14, 61)' }}>
            <Row style={{ alignItems: 'center' }}>
              <p
                style={{
                  flex: 1, padding: '0 1rem', margin: 0, color: 'rgb(187, 14, 61)',
                }}
                >
                {'Submission for this project has been disabled by the GDC. To continue your data submission process, please contact '}
                <EmailLink style={{ fontWeight: 400, color: '#2a72a5' }} url="mailto:support@nci-gdc.datacommons.io"> support@nci-gdc.datacommons.io</EmailLink>
                .
              </p>
            </Row>
          </Card>
        )}

        {project.released && (
          <Card>
            <Row style={{ alignItems: 'center' }}>
              <ReviewIcon style={{ marginRight: '1rem', color: '#a1882b', fontSize: '1.5rem' }} />
              <p style={{ flex: 1, padding: '0 1rem', margin: 0 }}>This project has been released.</p>
            </Row>
          </Card>
        )}

        {project.request_submission && (
          <Card>
            <Row style={{ alignItems: 'center' }}>
              <SubmittedIcon style={{ marginRight: '1rem', color: '#a1882b', fontSize: '1.5rem' }} />
              <p style={{ flex: 1, padding: '0 1rem', margin: 0 }}>
                The project is currently under review by the GDC for harmonization submission. Uploading new data is disabled during this review period. Once data is validated and submitted, the project will be re-opened by the GDC to allow the upload of new data.
              </p>
              <AuthorizedForProject
                permissions={['release']}
                projectId={projectId}
                >
                <Column style={{ justifyContent: 'space-between' }}>
                  <Tooltip Component="Harmonization Requested - Project in review by the GDC">
                    <SubmitButtonContainer
                      isDisabled={submitIsDisabled()}
                      params={router.params}
                      qcErrors={widgets.qcErrors}
                      style={{ marginBottom: '.25rem' }}
                      >
                      {project.request_submission ? 'HARMONIZATION REQUESTED' : submitIsDisabled() ? 'SUBMIT DISABLED' : 'REQUEST HARMONIZATION'}
                    </SubmitButtonContainer>
                  </Tooltip>
                  <OpenButtonContainer
                    isDisabled={!project.submission_enabled}
                    params={router.params}
                    >
                    Re-open
                  </OpenButtonContainer>
                </Column>
              </AuthorizedForProject>
            </Row>
          </Card>
        )}

        {!project.request_submission && project.in_review && !submitFailed && (
          <Card>
            <Row style={{ alignItems: 'center' }}>
              <ReviewIcon style={{ marginRight: '1rem', color: '#a1882b', fontSize: '1.5rem' }} />
              <p style={{ flex: 1, padding: '0 1rem', margin: 0 }}>
                The project is currently under review for harmonization submission. Uploading new data is disabled during this review period. The project can be re-opened to upload new data or submitted to begin processing.
              </p>
              <AuthorizedForProject
                permissions={['release']}
                projectId={projectId}
                >
                <Column style={{ justifyContent: 'space-between' }}>
                  <SubmitButtonContainer
                    isDisabled={submitIsDisabled()}
                    params={router.params}
                    qcErrors={widgets.qcErrors}
                    style={{ marginBottom: '.25rem' }}
                    >
                    {project.request_submission ? 'HARMONIZATION REQUESTED' : submitIsDisabled() ? 'SUBMIT DISABLED' : 'REQUEST HARMONIZATION'}
                  </SubmitButtonContainer>
                  <OpenButtonContainer
                    isDisabled={!project.submission_enabled}
                    params={router.params}
                    >
                    Re-open
                  </OpenButtonContainer>
                </Column>
              </AuthorizedForProject>
            </Row>
          </Card>
        )}

        {!project.request_submission && project.in_review && submitFailed && (
          <Card>
            <Row style={{ alignItems: 'center' }}>
              <ReviewIcon style={{ marginRight: '1rem', color: '#a1882b', fontSize: '1.5rem' }} />
              <p style={{ flex: 1, padding: '0 1rem', margin: 0 }}>
                This project is currently under review for harmonization submission, and the previous submit attempt failed. Please re-open
                to upload new data or try submitting again.
              </p>
              <AuthorizedForProject
                permissions={['release']}
                projectId={projectId}
                >
                <Column style={{ justifyContent: 'space-between' }}>
                  <SubmitButtonContainer
                    isDisabled={submitIsDisabled()}
                    params={router.params}
                    qcErrors={widgets.qcErrors}
                    style={{ marginBottom: '.25rem' }}
                    >
                    {project.request_submission ? 'HARMONIZATION REQUESTED' : submitIsDisabled() ? 'SUBMIT DISABLED' : 'REQUEST HARMONIZATION'}
                  </SubmitButtonContainer>
                  <OpenButtonContainer
                    isDisabled={!project.submission_enabled}
                    params={router.params}
                    >
                    Re-open
                  </OpenButtonContainer>
                </Column>
              </AuthorizedForProject>
            </Row>
          </Card>
        )}

        <Row style={{ justifyContent: 'space-between', margin: '0.5rem 1rem 1rem' }}>
          <h1
            style={{ fontFamily: 'inherit', fontWeight: 300, letterSpacing: 0, fontSize: '1.5rem' }}
          >
              Project Overview
          </h1>
          <DataDownloadModalButton style={{ margin: '0.8rem 0', paddingRight: '10px' }} />
        </Row>
        <Row>
          <DonutRow
            params={router.params}
            style={{ flex: 7 }}
            widgets={widgets}
            />
        </Row>

        <UploadAndValidate
          list={uploadTransactions}
          router={router}
          project={project}
          workflow={workflow}
          />

        <ReviewAndSubmit projectInfo={project} widgets={widgets} params={props.router.params} {...props} />

        <div
          className={`details-flyout ${Object.keys(details.item).length && did ? 'active' : ''}`}
          style={{
            position: 'fixed',
            right: 0,
            top: 0,
            zIndex: 100,
          }}
          >
          <TransactionDetails
            router={router}
            style={{ height: '100vh' }}
            >
            {details}
          </TransactionDetails>
        </div>
      </CenteredLayout>
    </Row>
  );
}

export { Dashboard as UnconnectedDashboard };

export default connect(
  ({
    widgets,
    projects,
    entityDefinitions,
    uploadTransactions,
    details,
    workflow,
  }) => ({
    widgets,
    projects,
    entityDefinitions,
    uploadTransactions,
    details,
    workflow,
  }),
)(withRouting(Dashboard));
